import React from 'react';
import _isEmpty from 'lodash/isEmpty';
import { FleetCapabilities } from '@bringg/types';
import Fleets, { Translations as FleetsTranslations } from './fleets/fleets';
import AddFleet, { Translations as AddFleetTranslations } from './add-fleet/add-fleet';
import Fallback, { Translations as FallbackTranslations } from './fallback/fallback';
import { FallbackMethod } from './fallback/use-fallback';

export interface PriorityConfigurationProps {
	fleets: FleetsType;
	selectedFleetIds: string[];
	isFallbackMethodEnabled: boolean;
	fallbackMethod: FallbackMethod;
	setIsFallbackMethodEnabled: (checked: boolean) => void;
	setFallbackMethod: (method: FallbackMethod) => void;
	onChange: (fleetIds: string[]) => void;
	translations: Translations;
	readOnly?: boolean;
}

export interface Fleet {
	id: string;
	name: string;
	capabilities?: FleetCapabilities;
	fleet_type?: number;
}

export type FleetsType = {
	[id: string]: Fleet;
};

export interface Translations {
	cheap: string;
	fast: string;
	priority: string;
	fallbackTooltip: string;
	fallbackHeader: string;
	noSelectedFleets: string;
	addFleet: string;
	noMoreFleetsToAdd: string;
	internalFleetName: string;
}

const PriorityConfiguration: React.FC<PriorityConfigurationProps> = ({
	fleets,
	selectedFleetIds,
	onChange,
	isFallbackMethodEnabled,
	fallbackMethod,
	setIsFallbackMethodEnabled,
	setFallbackMethod,
	translations,
	readOnly
}) => {
	const onAdd = id => {
		onChange([...selectedFleetIds, id]);
	};

	const onSelectedFleetsChange = fleets => {
		const fleetIds = fleets.map(fleet => fleet.id);
		onChange(fleetIds);
	};

	const selectedFleets = selectedFleetIds.map(id => ({
		id,
		name: fleets[id].name,
		fleet_type: fleets[id].fleet_type,
		capabilities: fleets[id].capabilities
	}));
	const notSelectedFleets = Object.values<Fleet>(fleets).filter(fleet => !selectedFleetIds.includes(fleet.id));

	const addFleetTranslations: AddFleetTranslations = {
		addFleet: translations.addFleet,
		noMoreFleetsToAdd: translations.noMoreFleetsToAdd
	};

	const fleetsTranslations: FleetsTranslations = {
		internalFleetName: translations.internalFleetName
	};

	const fallbackTranslations: FallbackTranslations = {
		fallbackHeader: translations.fallbackHeader,
		fallbackTooltip: translations.fallbackTooltip,
		cheap: translations.cheap,
		fast: translations.fast,
		priority: translations.priority
	};

	return (
		<div className="fleet-group-section-wrapper">
			<div className="fleets-section">
				{_isEmpty(selectedFleets) ? (
					<div className="no-selected-fleets-message">{translations.noSelectedFleets}</div>
				) : (
					<Fleets
						fleets={selectedFleets}
						onChange={onSelectedFleetsChange}
						translations={fleetsTranslations}
						readOnly={readOnly}
					/>
				)}
			</div>
			<AddFleet
				fleets={notSelectedFleets}
				onChange={onAdd}
				translations={addFleetTranslations}
				disabled={readOnly}
			/>
			<Fallback
				fallbackMethod={fallbackMethod}
				isEnabled={isFallbackMethodEnabled}
				onChangeEnabled={setIsFallbackMethodEnabled}
				onChangeFallback={setFallbackMethod}
				translations={fallbackTranslations}
				readOnly={readOnly}
			/>
		</div>
	);
};

export default PriorityConfiguration;
